import { Button, ButtonSizes } from "@inno-ui/button";
import { ChevronLeftIcon } from "@inno-ui/icons";
import { toast } from "@inno-ui/toast";
import { useEffect, useMemo, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { useBookEventSlot } from "../../../../../hooks/api/use-book-event-slot";
import { useAppForm } from "../../../../../hooks/use-form";
import { useSelectedDate } from "../../../../../hooks/use-selected-date";
import { useAppointmentStore } from "../../../../../store/appointment/appointment-store";
import { CONTACT_FORM_INITIAL_VALUES } from "../../../../../store/appointment/appointment-store.constants";
import { useGeneralStore } from "../../../../../store/general/general-store";
import { externalRedirectUrl, extractUrlFormParams } from "../../../../../utils/helpers/general";
import { useGenderOptions } from "./appointment-form.constant";
import styles from "./appointment-form.module.scss";
import { createAppointmentFormSchema } from "./appointment-form.schema";

export function AppointmentForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const GENDER_OPTIONS = useGenderOptions();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const referrer = searchParams.get("referrer");
  const isRejectionUrl = location.pathname.split("/")[1].toLowerCase() === "rej";
  const { t } = useTranslation();

  const {
    setContactDetails,
    currentEventTypeId,
    otherParticipants,
    userAppointmentData,
  } = useAppointmentStore();
  const { mutateAsync: bookEventSlot } = useBookEventSlot();
  const { onNext, onPrev, currentTimezone, language } = useGeneralStore();
  const { fromFormatted, toFormatted } = useSelectedDate();
  const [customFieldsTitle, setCustomFieldsTitle] = useState<string[]>([]);
  const [customFieldsKeys, setCustomFieldsKeys] = useState<string[]>([]);

  const customFieldsMemoized = useMemo(
    () =>
      JSON.parse(userAppointmentData?.extra?.event_type?.payload ?? "{}")?.custom_fields ?? [],
    [userAppointmentData?.extra?.event_type?.payload],
  );

  const appointmentFormSchema = useMemo(
    () => createAppointmentFormSchema(customFieldsMemoized),
    [customFieldsMemoized],
  );

  // Extract URL parameters for auto-filling
  const urlParams = extractUrlFormParams();

  // Merge URL parameters with initial values
  const initialFormValues = {
    ...CONTACT_FORM_INITIAL_VALUES,
    ...urlParams,
  };

  const form = useAppForm({
    defaultValues: initialFormValues,
    onSubmit: async ({ value }) => {
      const captchaToken = await executeRecaptcha?.("book_appointment");

      const contactDetails = {
        ...value,
        id: Math.random().toString(),
        gender: value.gender.value,
        phone: value.phone,
        // tells the backend that this is the main initiator of the meeting
        isBooker: true,
      };

      // add isBooker: false to each element of otherParticipants
      const otherParticipantsWithIsBooker = otherParticipants.map(
        participant => ({
          ...participant,
          isBooker: false,
        }),
      );

      // make payload in the form like {field1: 'hello', field2: 'world'}
      const payload = customFieldsKeys?.reduce(
        (acc, field, index) => ({ ...acc, [field]: value.customFields[index] }),
        {},
      );

      setContactDetails(contactDetails);
      const response = await bookEventSlot({
        event_type_id: currentEventTypeId,
        from: fromFormatted,
        to: toFormatted,
        timezone: currentTimezone,
        attendees: [contactDetails, ...otherParticipantsWithIsBooker],
        payload: JSON.stringify(payload),
        lang: language,
        // @ts-ignore
        feedback: value.message,
        recaptcha_token: captchaToken ?? "",
      });

      if (response.code === 200) {
        // add ?booked=true to the url in every case except for rejection url
        if (!isRejectionUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set("booked", "true");
          window.history.replaceState({}, "", url.toString());
        }

        // if there's a referrer, redirect to the external url
        if (referrer) {
          const redirectUrl = externalRedirectUrl(response?.data?.calendar_event_uuid, language);
          window.location.replace(redirectUrl);
          return;
        }
        return onNext?.();
      }
      // get the first object key value
      const error = typeof response?.error === "string" ? response.error : Object?.values(response?.error ?? {})[0];
      toast.error({ title: error as string });
    },
    validators: {
      onSubmit: appointmentFormSchema,
    },
  });

  useEffect(() => {
    const customFieldsTitleArr = customFieldsMemoized?.map(
      (field: any) => field.title,
    );
    const customFieldsKeysArr = customFieldsMemoized?.map(
      (field: any) => field.key,
    );
    const customFields = Array.from({
      length: customFieldsTitleArr?.length ?? 0,
    }).fill("");
    setCustomFieldsTitle(customFieldsTitleArr);
    setCustomFieldsKeys(customFieldsKeysArr);
    form.setFieldValue("customFields", customFields as string[]);
  }, [customFieldsMemoized, form]);

  // Auto-fill form fields from URL parameters on component mount
  useEffect(() => {
    const urlParams = extractUrlFormParams();
    if (urlParams.firstName)
      form.setFieldValue("firstName", urlParams.firstName);
    if (urlParams.lastName)
      form.setFieldValue("lastName", urlParams.lastName);
    if (urlParams.email)
      form.setFieldValue("email", urlParams.email);
    if (urlParams.phone)
      form.setFieldValue("phone", urlParams.phone);
    if (urlParams.gender.value)
      form.setFieldValue("gender", urlParams.gender);
    if (urlParams.message)
      form.setFieldValue("message", urlParams.message);
  }, [form]);

  return (
    <form
      className={styles.appointmentForm}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className={styles.formWrapper}>
        <div className={styles.formRow}>
          {/* first name */}
          <form.AppField name="firstName">
            {field => (
              <field.TextInputField
                placeholder={t("please-enter")}
                label={t("first-name")}
              />
            )}
          </form.AppField>
          {/* last name */}
          <form.AppField name="lastName">
            {field => (
              <field.TextInputField
                placeholder={t("please-enter")}
                label={t("last-name")}
              />
            )}
          </form.AppField>
        </div>
        <div className={styles.formRow}>
          {/* email */}
          <form.AppField name="email">
            {field => (
              <field.TextInputField
                placeholder={t("please-enter")}
                label={t("email")}
              />
            )}
          </form.AppField>
        </div>
        <div className={styles.formRow}>
          {/* gender select */}
          <form.AppField name="gender">
            {(field) => {
              return (
                <field.SelectInputField
                  placeholder={t("please-select")}
                  label={t("gender")}
                  options={GENDER_OPTIONS}
                  width="100%"
                />
              );
            }}
          </form.AppField>
          <form.AppField name="phone">
            {field => <field.PhoneInputField label={t("phone")} width="100%" />}
          </form.AppField>
        </div>
        {/* custom fields */}
        {!!customFieldsTitle?.length && (
          <div className={styles.customFieldsContainer}>
            <form.Field
              name="customFields"
              mode="array"
              children={field =>
                field?.state?.value?.map((_, index) => {
                  return (
                    <form.AppField
                      key={`customFields[${index}]`}
                      name={`customFields[${index}]`}
                    >
                      {field => (
                        <field.TextInputField
                          placeholder={t("please-enter")}
                          label={customFieldsTitle[index]}
                        />
                      )}
                    </form.AppField>
                  );
                })}
            />
          </div>
        )}
        {/* message */}
        <div className={styles.formRow}>
          <form.AppField name="message">
            {field => (
              <field.TextAreaField
                placeholder={t("please-enter")}
                label={t("message")}
              />
            )}
          </form.AppField>
        </div>
      </div>
      <div className={styles.formActions}>
        <Button icon={ChevronLeftIcon} label={t("back")} width="100%" size={ButtonSizes.L} onClick={onPrev} />
        <form.AppForm>
          <div className={styles.buttonWrapper}>
            <form.SubmitButton label={t("book-appointment")} />
          </div>
        </form.AppForm>
      </div>
    </form>
  );
}
